import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useKotakCropStore, kotakSides } from '@renderer/stores/kotakCropStore'
import type { CropRect } from '@renderer/utils/kotakCrop'
import Preview from '@renderer/components/Preview'
import Button from '@renderer/components/bahela/Button'
import { stateUploader } from '@renderer/utils/pipeline/outputOrchestrator'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

import bgLayer from '/assets/bahela/bg-layer.webp'
import bahelaLogo from '/assets/bahela/bahela.webp'

type BoxKey = 'primary' | 'secondary'
type Selection = Record<BoxKey, boolean>

/**
 * Bahelakotak — "Which boxes will you use?" step.
 *
 * The kotak booth has two physical boxes, one camera each (primary = left, secondary = right).
 * A group uses both; a solo guest uses one. This page lets the guest tick the box(es) they'll
 * stand in (checkbox style — one OR both, never zero) and records the choice on the session:
 *   - both ticked  → dual capture (the custom kotak Capture/Retake pages),
 *   - one ticked   → single capture via the REGULAR bahela pages, using the ticked camera.
 *
 * TRANSPARENCY: we ALWAYS render both slots and show each camera's real connection state. If the
 * secondary was unplugged by accident, the guest/operator sees a clear "not connected" slot
 * instead of a mysterious blank — and can reconnect it live (the slot updates reactively). Only
 * connected cameras are selectable; a disconnected one simply can't be chosen. Reaching this page
 * at all is already gated on the dual_camera flag (bahela/Welcome), so single-camera booths never
 * see it.
 */
const CameraPage: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { i18n } = useTranslation()
  const { setSessionState } = useSessionStateStore()
  const { config } = useConfigStore()

  // Live connection state per camera (reactive — reconnecting a camera updates the slot).
  const webcam = useCameraStore((s) => s.webcam)
  const camera = useCameraStore((s) => s.camera)
  const secondaryWebcam = useCameraStore((s) => s.secondaryWebcam)
  const secondaryCamera = useCameraStore((s) => s.secondaryCamera)
  const connected: Selection = {
    primary: Boolean(webcam || camera),
    secondary: Boolean(secondaryWebcam || secondaryCamera)
  }

  const welcomeSettings = config?.pages?.welcome?.settings
  const startWithPayment = welcomeSettings?.startWithPayment || false
  const nextPage = startWithPayment ? 'payment' : 'filter'

  // Same per-project crop the capture page uses, so each preview matches its real box window.
  const projectKey = projectId || 'default'
  const cropRatio = useKotakCropStore((s) => s.getProjectCrop(projectKey).ratio)
  const savedPrimary = useKotakCropStore((s) => s.getProjectCrop(projectKey).primary)
  const savedSecondary = useKotakCropStore((s) => s.getProjectCrop(projectKey).secondary)
  const savedRects: Record<BoxKey, CropRect | undefined> = {
    primary: savedPrimary,
    secondary: savedSecondary
  }
  // Honor the operator's L/R swap so the left tile shows the left-box camera.
  const swapped = useKotakCropStore((s) => s.getProjectCrop(projectKey).swapped)
  const { leftKey, rightKey } = kotakSides(swapped)

  // The guest's INTENT to use a box; only meaningful for a connected camera. Defaults to both.
  const [intent, setIntent] = useState<Selection>({ primary: true, secondary: true })
  // Effective selection = intended AND actually connected.
  const effective: Selection = {
    primary: intent.primary && connected.primary,
    secondary: intent.secondary && connected.secondary
  }
  const effectiveCount = (['primary', 'secondary'] as BoxKey[]).filter((k) => effective[k]).length
  const effectiveRef = useRef<Selection>(effective)
  effectiveRef.current = effective

  const timeout = config?.pages?.camera?.settings?.cameraTimeout || 30
  const countdown = useCountdown(timeout)
  const [remaining, setRemaining] = useState<number>(timeout)

  const commit = (sel: Selection, replace = false): void => {
    const boxes = (['primary', 'secondary'] as BoxKey[]).filter((k) => sel[k])
    if (boxes.length === 0) return
    const single = boxes.length === 1
    setSessionState({
      userPreferences: {
        kotakSingleCamera: single,
        selectedCamera: single ? boxes[0] : 'primary'
      }
    })
    stateUploader.uploadSessionState()
    navigate(`/${projectId}/${nextPage}`, { replace })
  }

  // Auto-continue on timeout with whatever is effectively selected (never with zero cameras).
  useEffect(() => {
    countdown.setOnTick(() => setRemaining(countdown.getRemainingSeconds()))
    countdown.setOnComplete(() => {
      const eff = effectiveRef.current
      if ((['primary', 'secondary'] as BoxKey[]).some((k) => eff[k])) commit(eff, true)
    })
    countdown.start()
    return () => countdown.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const toggle = (key: BoxKey): void => {
    if (!connected[key]) return // can't select a disconnected camera
    setIntent((prev) => {
      const next = { ...prev, [key]: !prev[key] }
      // Never leave zero CONNECTED boxes selected.
      const stillHasOne =
        (next.primary && connected.primary) || (next.secondary && connected.secondary)
      return stillHasOne ? next : prev
    })
  }

  const cachedBgLayer = useCachedBackgroundImage(bgLayer)
  const cachedBahelaLogo = useCachedImage(bahelaLogo)

  const anyDisconnected = !connected.primary || !connected.secondary

  const paneFrameClass =
    'relative shrink-0 overflow-hidden rounded-[28px] border-[6px] bg-black shadow-[inset_0_0_40px_rgba(0,0,0,0.8)] transition-all duration-200'
  const paneStyle: React.CSSProperties = {
    aspectRatio: String(cropRatio),
    // Cap by BOTH height and width and leave headroom so the whole page (title, boxes, summary,
    // button) fits inside the safe-area padding on a wide/short booth monitor — not just a laptop.
    height: `min(40vh, calc((34vw) / ${cropRatio}))`
  }

  const renderBox = (key: BoxKey, label: string, cropRect: typeof savedPrimary): React.ReactNode => {
    const isConnected = connected[key]
    const isOn = effective[key]
    return (
      <div className="flex flex-col items-center gap-[min(1.5vh,16px)]">
        <button
          type="button"
          onClick={() => toggle(key)}
          disabled={!isConnected}
          className={`${paneFrameClass} ${isConnected ? 'cursor-pointer' : 'cursor-not-allowed'}`}
          style={{
            ...paneStyle,
            borderColor: !isConnected
              ? 'rgba(255,90,90,0.55)'
              : isOn
                ? '#F2C230'
                : 'rgba(255,255,255,0.25)',
            opacity: !isConnected ? 0.85 : isOn ? 1 : 0.4,
            transform: isOn ? 'scale(1)' : 'scale(0.96)'
          }}
        >
          {isConnected ? (
            <>
              <Preview
                key={`kotak-pick-${key}`}
                cameraKey={key}
                ratio={cropRatio}
                cropRect={cropRect}
                fillContainer
              />
              {!isOn && <div className="absolute inset-0 bg-black/45" />}
              <div
                className={`absolute top-4 right-4 z-[3] w-[54px] h-[54px] rounded-full border-4 flex items-center justify-center ${
                  isOn ? 'bg-uriYellow border-uriYellow' : 'bg-black/40 border-white/70'
                }`}
              >
                {isOn && <span className="text-hibiscus text-3xl leading-none">✓</span>}
              </div>
            </>
          ) : (
            // Disconnected: honest empty state instead of a blank/broken preview.
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/70 text-center px-6">
              <span className="text-[64px] leading-none">📷</span>
              <span className="font-serif-bahela text-red-300 text-[34px] leading-tight">
                {i18n.language === 'id' ? 'Kamera tidak terhubung' : 'Camera not connected'}
              </span>
              <span className="font-body-bahela text-white/70 text-[22px] leading-tight">
                {i18n.language === 'id' ? 'Hubungi petugas' : 'Please ask our staff'}
              </span>
            </div>
          )}
        </button>
        <span
          className={`font-serif-bahela text-[clamp(26px,4vh,46px)] leading-none ${
            !isConnected ? 'text-red-300/80' : isOn ? 'text-uriYellow' : 'text-white/50'
          }`}
        >
          {label}
        </span>
      </div>
    )
  }

  const summary = ((): string => {
    if (effectiveCount === 0) {
      return i18n.language === 'id'
        ? 'Tidak ada kamera terhubung — hubungi petugas.'
        : 'No camera connected — please ask our staff.'
    }
    if (effectiveCount === 2) {
      return i18n.language === 'id'
        ? 'Dua bilik — dua kamera memotret bersamaan.'
        : 'Two boxes — both cameras shoot together.'
    }
    // Which side the single chosen camera sits on depends on the L/R swap.
    const chosenIsLeft = effective[leftKey]
    const box = chosenIsLeft
      ? i18n.language === 'id'
        ? 'Kiri'
        : 'Left'
      : i18n.language === 'id'
        ? 'Kanan'
        : 'Right'
    return i18n.language === 'id' ? `Satu bilik (${box}) — foto sendiri.` : `One box (${box}) — solo shoot.`
  })()

  return (
    <div className="relative bg-hibiscus w-full h-full overflow-hidden">
      {/* Background layer */}
      <div
        className="absolute top-0 left-0 w-full h-full bg-cover z-[0]"
        style={{ backgroundImage: `url(${cachedBgLayer})` }}
      />

      {/* Full-height, centered, with generous safe-area padding so nothing hugs the monitor
          edges (a wide booth display is much shorter than a laptop). Font sizes scale with the
          viewport height (clamped) so the layout stays balanced from laptop to large booth TV. */}
      <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-[min(3vh,32px)] px-[5vw] py-[5vh]">
        {/* Title + subtitle */}
        <div className="flex flex-col items-center gap-[min(1.2vh,12px)] text-center">
          <h1 className="font-serif-bahela text-white text-[clamp(40px,6.4vh,76px)] leading-none drop-shadow-lg">
            {i18n.language === 'id' ? 'Pakai Bilik yang Mana?' : 'Which Boxes Will You Use?'}
          </h1>
          <p className="font-body-bahela text-uriYellow text-[clamp(20px,3vh,34px)] leading-tight max-w-4xl">
            {i18n.language === 'id'
              ? 'Datang sendiri? Pilih satu bilik saja. Berdua? Pilih keduanya.'
              : 'Here alone? Tick one box. Together? Tick both.'}
          </p>
        </div>

        {/* Box choices — ordered/labeled by the operator's L/R swap. */}
        <div className="flex items-start justify-center gap-[min(6vw,70px)]">
          {renderBox(leftKey, i18n.language === 'id' ? 'Kiri' : 'Left', savedRects[leftKey])}
          {renderBox(rightKey, i18n.language === 'id' ? 'Kanan' : 'Right', savedRects[rightKey])}
        </div>

        {/* Selection / connection summary */}
        <p
          className={`font-body-bahela text-[clamp(18px,2.6vh,30px)] leading-none ${
            anyDisconnected ? 'text-red-200' : 'text-white/90'
          }`}
        >
          {summary}
        </p>

        {/* Continue (disabled only in the extreme no-camera case) */}
        <Button
          onClick={() => commit(effective)}
          className={`text-[clamp(30px,4.8vh,54px)] ${effectiveCount === 0 ? 'opacity-40' : 'opacity-100'}`}
          textColor="hibiscus"
          disabled={effectiveCount === 0}
        >
          {i18n.language === 'id' ? 'Lanjut' : 'Continue'} ({remaining})
        </Button>
      </div>

      {/* Bahela logo */}
      <img
        src={cachedBahelaLogo}
        alt="Bahela Logo"
        className="absolute z-[1] bottom-[66px] left-[57px] w-[127px] h-[114px]"
      />
    </div>
  )
}

export default CameraPage
